import DOMPurify from 'dompurify'

/**
 * sanitizeContent(html, options)
 *
 * Default-safe sanitizer that preserves tables and (optionally) iframes from a
 * known set of hosts (e.g., YouTube/Vimeo). Anything else is stripped.
 *
 * Usage from viewers:
 *   sanitizeContent(html, {
 *     addTags: ['iframe'],
 *     addAttr: ['allow','allowfullscreen','referrerpolicy','frameborder','width','height','style','class'],
 *     addIframeHostnames: [
 *       'www.youtube-nocookie.com',
 *       'www.youtube.com',
 *       'player.vimeo.com',
 *       'i.ytimg.com',
 *       's.ytimg.com',
 *     ]
 *   })
 */

const BASE_ADD_TAGS = [
  // rich content we allow
  'iframe',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'colgroup', 'col',
]

const BASE_ADD_ATTR = [
  // generic formatting
  'class', 'style',
  // table basics
  'rowspan', 'colspan', 'align', 'valign', 'border', 'cellpadding', 'cellspacing', 'scope',
  'width', 'height', 'data-display',
  // iframe specifics we support
  'src', 'allow', 'allowfullscreen', 'referrerpolicy', 'frameborder', 'scrolling'
]

function buildConfig(opts = {}) {
  const addTags = Array.from(new Set([...(opts.addTags || []), ...BASE_ADD_TAGS]))
  const addAttr = Array.from(new Set([...(opts.addAttr || []), ...BASE_ADD_ATTR]))

  return {
    ADD_TAGS: addTags,
    ADD_ATTR: addAttr,
    // Keep DOMPurify strict; we explicitly post-filter iframes below
    // to only allow src hostnames from opts.addIframeHostnames.
  }
}

function hostnameAllowed(url, allowList) {
  try {
    const u = new URL(url, window.location.origin)
    const h = (u.hostname || '').toLowerCase()
    return allowList.some(allowed => {
      // exact match or wildcard like *.googlevideo.com
      if (allowed.startsWith('*.')) {
        const root = allowed.slice(2).toLowerCase()
        return h === root || h.endsWith('.' + root)
      }
      return h === allowed.toLowerCase()
    })
  } catch {
    return false
  }
}

export function sanitizeContent(dirty, opts = {}) {
  const cfg = buildConfig(opts)
  const cleaned = DOMPurify.sanitize(dirty || '', cfg)

  // Post-process in a detached DOM to (a) harden links and (b) strictly filter iframes by hostname
  const container = document.createElement('div')
  container.innerHTML = cleaned.toString()

  // Harden external anchors
  container.querySelectorAll('a[href]').forEach(a => {
    try {
      const href = a.getAttribute('href') || ''
      if (!href) return
      const u = new URL(href, window.location.origin)
      if (/^https?:\/\//i.test(u.href) && !u.href.startsWith(window.location.origin)) {
        if (!a.hasAttribute('rel')) a.setAttribute('rel', 'noopener noreferrer')
        // Optional: enforce new tab
        // if (!a.hasAttribute('target')) a.setAttribute('target', '_blank')
      }
    } catch {
      // ignore malformed href
    }
  })

  // Strictly vet iframes
  const allowHosts = Array.isArray(opts.addIframeHostnames) ? opts.addIframeHostnames : []
  container.querySelectorAll('iframe').forEach(frame => {
    const src = frame.getAttribute('src') || ''
    const ok = src && allowHosts.length > 0 && hostnameAllowed(src, allowHosts)
    if (!ok) {
      // Drop unknown/empty src iframes completely
      frame.remove()
      return
    }

    // Normalize some safe defaults to avoid mixed content & referrer leakage
    frame.setAttribute('referrerpolicy', frame.getAttribute('referrerpolicy') || 'strict-origin-when-cross-origin')
    frame.setAttribute('loading', frame.getAttribute('loading') || 'lazy')
    // Do not force width/height here; let upstream styles control sizing
  })

  return container.innerHTML
}

export default sanitizeContent
